import { MapPin, Clock, Navigation, MessageCircle } from "lucide-react";
import Reveal from "./Reveal";
import { BRAND } from "../lib/brand";

/**
 * LocationSection — a "location cube" with map + address + hours + actions.
 * Used on Home and Contact pages.
 */
export default function LocationSection({ variant = "light" }) {
  const isDark = variant === "dark";

  return (
    <section
      className={`relative py-20 md:py-28 overflow-hidden ${
        isDark ? "bg-[#0a1530] text-white" : "bg-[#faf8f5] text-[#1a2e5a]"
      }`}
      data-testid="location-section"
    >
      <div className={`absolute inset-0 ${isDark ? "mandala-bg opacity-[0.05]" : "spice-dots opacity-30"} pointer-events-none`} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
          <Reveal>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#c9a84c] font-bold mb-4">
              Find Us · Visit · Direct
            </div>
          </Reveal>
          <Reveal delay={1}>
            <h2 className={`font-display text-4xl md:text-5xl font-bold leading-tight ${isDark ? "text-white" : "text-[#1a2e5a]"}`}>
              The kitchen lives in <span className="italic text-[#c9a84c]">Dwarka.</span>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className={`font-body text-lg mt-5 ${isDark ? "text-white/70" : "text-slate-600"}`}>
              We cook in our own home kitchen — and deliver fresh across Delhi.
              Drop by, message us, or just send a WhatsApp and we'll handle the rest.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">
          {/* Map cube */}
          <Reveal className="lg:col-span-7">
            <div
              className="card-lift relative rounded-3xl overflow-hidden border border-[#c9a84c]/30 shadow-2xl bg-white h-[360px] sm:h-[440px] lg:h-full min-h-[360px]"
              data-testid="location-map"
            >
              <iframe
                title="Archna's Kitchen — Sector 19 Dwarka, Delhi"
                src={BRAND.mapEmbedUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full border-0"
              />
              {/* Pin badge overlay */}
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#1a2e5a] text-[#c9a84c] text-[11px] tracking-[0.22em] uppercase font-bold px-3 py-2 rounded-full shadow-lg pointer-events-none">
                <MapPin size={14} />
                Sector 19, Dwarka
              </div>
            </div>
          </Reveal>

          {/* Info card */}
          <Reveal delay={1} className="lg:col-span-5">
            <div
              className={`card-lift h-full rounded-3xl p-7 md:p-9 border ${
                isDark
                  ? "bg-[#132345] border-[#c9a84c]/30 text-white"
                  : "bg-white border-[#c9a84c]/30 text-[#1a2e5a]"
              }`}
              data-testid="location-info-card"
            >
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a84c] font-bold mb-3">
                Our Address
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold leading-snug mb-4">
                {BRAND.legalName}
              </h3>
              <p className={`font-body text-[15px] leading-relaxed mb-7 ${isDark ? "text-white/80" : "text-slate-700"}`}>
                {BRAND.address.line1}
                <br />
                {BRAND.address.line2}
                <br />
                {BRAND.address.city} — {BRAND.address.pin}
              </p>

              <div className={`rounded-2xl p-5 mb-7 ${isDark ? "bg-white/5 border border-[#c9a84c]/20" : "bg-[#faf8f5] border border-[#c9a84c]/30"}`}>
                <div className="flex items-center gap-2 text-[#c9a84c] text-[11px] tracking-[0.25em] uppercase font-bold mb-3">
                  <Clock size={14} /> Kitchen Hours
                </div>
                <ul className="space-y-2 text-sm">
                  {BRAND.hours.map((h) => (
                    <li key={h.day} className="flex items-center justify-between gap-4">
                      <span className={`font-body ${isDark ? "text-white/80" : "text-slate-700"}`}>
                        {h.day}
                      </span>
                      <span className={`font-display font-semibold ${isDark ? "text-white" : "text-[#1a2e5a]"}`}>
                        {h.time}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={BRAND.mapDirectionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold flex-1"
                  data-testid="location-directions-cta"
                >
                  <Navigation size={16} strokeWidth={2.5} />
                  Get Directions
                </a>
                <a
                  href={BRAND.whatsappChat}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={isDark ? "btn-ghost-gold flex-1" : "btn-navy flex-1"}
                  data-testid="location-whatsapp-cta"
                >
                  <MessageCircle size={16} strokeWidth={2.5} />
                  WhatsApp
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
